import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { getUser } from '@/utils/auth';
import { Upload, Image as ImageIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Komponen untuk load gambar yang butuh Authorization header
// (browser tidak bisa nyisipin header custom ke <img src="...">,
// jadi kita fetch manual pakai axios instance yang sudah ada interceptor token-nya,
// lalu convert response jadi blob URL)
function AuthImage({ path, alt, className }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl;
    let cancelled = false;

    const loadImage = async () => {
      setFailed(false);
      setSrc(null);
      try {
        const cleanPath = path.replace(/^\/api\//, '/').replace(/^\//, '');
        const response = await api.get(`/screenshots/${cleanPath}`, {
          responseType: 'blob',
        });
        objectUrl = URL.createObjectURL(response.data);
        if (!cancelled) {
          setSrc(objectUrl);
        }
      } catch (error) {
        console.error('Gagal memuat screenshot:', error);
        if (!cancelled) setFailed(true);
      }
    };

    if (path) loadImage();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  if (failed) {
    return (
      <div className={`${className} bg-muted flex items-center justify-center`}>
        <ImageIcon className="w-8 h-8 text-muted-foreground" />
      </div>
    );
  }

  if (!src) {
    return (
      <div className={`${className} bg-muted animate-pulse flex items-center justify-center`}>
        <ImageIcon className="w-8 h-8 text-muted-foreground opacity-50" />
      </div>
    );
  }

  return <img src={src} alt={alt} className={className} />;
}

// Format Date -> 'YYYY-MM-DD' pakai komponen tanggal lokal (bukan toISOString,
// yang bisa geser sehari kalau timezone browser di belakang UTC).
function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function defaultDateRange() {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  return {
    fromDate: toDateInputValue(yesterday),
    toDate: toDateInputValue(today),
  };
}

export default function Screenshots() {
  const user = getUser();
  const canFilterByEmployee = user?.role === 'admin' || user?.role === 'security_manager';

  const [screenshots, setScreenshots] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [{ fromDate, toDate }, setDateRange] = useState(defaultDateRange);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  // Re-fetch screenshots setiap kali filter employee atau date range berubah (filter di server, bukan client)
  useEffect(() => {
    if (fromDate && toDate && fromDate > toDate) {
      toast.error('Tanggal mulai tidak boleh melebihi tanggal selesai');
      return;
    }
    fetchScreenshots(selectedEmployee, fromDate, toDate);
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee, fromDate, toDate]);

  // Ambil daftar employee sekali saja (ringan, tidak narik semua screenshot)
  useEffect(() => {
    if (canFilterByEmployee) {
      fetchEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchScreenshots = async (employeeFilter, from, to) => {
    setLoading(true);
    try {
      const params = {};
      if (employeeFilter && employeeFilter !== 'all') {
        params.user_id = employeeFilter;
      }
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      const response = await api.get('/screenshots', { params });
      setScreenshots(response.data);
    } catch (error) {
      toast.error('Gagal memuat screenshots');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/screenshots/employees');
      setEmployees(response.data);
    } catch (error) {
      // Non-fatal: dropdown filter cuma jadi kosong
      console.error('Gagal memuat daftar employee', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowedTypes.includes(file.type) && !file.type.startsWith('image/')) {
      toast.error('Hanya file gambar (JPG, PNG, WebP) yang diperbolehkan');
      e.target.value = '';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Ukuran file maksimal 10MB');
      e.target.value = '';
      return;
    }

    if (uploading) {
      e.target.value = '';
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post('/screenshots/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Screenshot berhasil diupload!');
      fetchScreenshots(selectedEmployee, fromDate, toDate);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal upload screenshot');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const totalPages = Math.ceil(screenshots.length / pageSize) || 1;
  const paginatedScreenshots = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return screenshots.slice(start, start + pageSize);
  }, [screenshots, currentPage, pageSize]);

  if (loading && screenshots.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="pb-2 border-b border-slate-200/70 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 tracking-tight" data-testid="screenshots-page-title">Screenshots</h1>
          <p className="text-muted-foreground text-sm mt-1">Upload dan lihat aktivitas kerja</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-2.5 py-1.5 shadow-sm">
            <input
              type="date"
              value={fromDate}
              max={toDate}
              onChange={(e) => setDateRange((prev) => ({ ...prev, fromDate: e.target.value }))}
              className="text-xs text-slate-700 outline-none bg-transparent w-[130px]"
              data-testid="screenshot-from-date-input"
            />
          </div>
          <span className="text-xs text-slate-400 font-medium">s/d</span>
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-2.5 py-1.5 shadow-sm">
            <input
              type="date"
              value={toDate}
              min={fromDate}
              onChange={(e) => setDateRange((prev) => ({ ...prev, toDate: e.target.value }))}
              className="text-xs text-slate-700 outline-none bg-transparent w-[130px]"
              data-testid="screenshot-to-date-input"
            />
          </div>
          {canFilterByEmployee && (
            <Select
              value={selectedEmployee}
              onValueChange={setSelectedEmployee}
            >
              <SelectTrigger className="w-[200px] bg-white border-slate-200 rounded-xl text-sm" data-testid="employee-filter-select">
                <SelectValue placeholder="Filter Employee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Employee</SelectItem>
                {employees.map((emp) => (
                  <SelectItem key={emp.user_id} value={emp.user_id}>
                    {emp.user_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div>
            <input
              type="file"
              id="screenshot-upload"
              data-testid="screenshot-upload-input"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              onClick={() => document.getElementById('screenshot-upload').click()}
              data-testid="upload-screenshot-button"
              disabled={uploading}
              className="bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 active:scale-[0.99]"
            >
              <Upload className="w-5 h-5 mr-2" />
              {uploading ? 'Mengupload...' : 'Upload Screenshot'}
            </Button>
          </div>
        </div>
      </div>

      {/* Screenshots Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1A3C34]"></div>
        </div>
      ) : screenshots.length === 0 ? (
        <Card className="p-12 bg-white border-slate-200/80 shadow-sm">
          <div className="text-center space-y-4">
            <div className="inline-flex p-6 rounded-full bg-slate-100">
              <ImageIcon className="w-12 h-12 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Belum Ada Screenshot</h3>
            <p className="text-muted-foreground text-sm">
              {selectedEmployee === 'all'
                ? 'Tidak ada screenshot pada rentang tanggal ini'
                : 'Tidak ada screenshot untuk employee ini pada rentang tanggal ini'}
            </p>
          </div>
        </Card>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Menampilkan {paginatedScreenshots.length} dari {screenshots.length} screenshot
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {paginatedScreenshots.map((screenshot) => (
            <Card
              key={screenshot.id}
              data-testid={`screenshot-card-${screenshot.id}`}
              className="overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer bg-white border-slate-200/80 shadow-sm"
              onClick={() => setSelectedImage(screenshot)}
            >
              <div className="aspect-video bg-slate-100 relative overflow-hidden">
                <AuthImage
                  path={screenshot.file_path}
                  alt="Screenshot"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4">
                <p className="font-semibold text-slate-900 text-sm">{screenshot.user_name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xs text-muted-foreground">
                    {new Date(screenshot.uploaded_at).toLocaleString('id-ID')}
                  </p>
                  {screenshot.active_percentage !== null && (
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      screenshot.active_percentage >= 80 
                        ? 'bg-emerald-100 text-emerald-700'
                        : screenshot.active_percentage >= 50
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      <div className="w-2 h-2 rounded-full" style={{ 
                        background: screenshot.active_percentage >= 80 ? '#10b981' : screenshot.active_percentage >= 50 ? '#f59e0b' : '#ef4444',
                        transform: 'rotate(45deg)'
                      }}></div>
                      {screenshot.active_percentage.toFixed(0)}%
                    </span>
                  )}
                </div>
              </div>
            </Card>
            ))}
          </div>
          {screenshots.length > pageSize && (
            <div className="flex flex-col sm:flex-row items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-2xl gap-3">
              <p className="text-xs text-muted-foreground">
                Hal {currentPage} / {totalPages} — Menampilkan {(currentPage - 1) * pageSize + 1} -{' '}
                {Math.min(currentPage * pageSize, screenshots.length)} dari {screenshots.length}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  className="h-8 px-2.5 text-xs bg-white rounded-xl border-slate-200"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Sebelumnya
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  className="h-8 px-2.5 text-xs bg-white rounded-xl border-slate-200"
                >
                  Berikutnya <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="screenshot-modal"
        >
          <div className="max-w-5xl w-full">
            <Card className="overflow-hidden bg-white border-0 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <AuthImage
                path={selectedImage.file_path}
                alt="Screenshot"
                className="w-full h-auto"
              />
              <div className="p-4">
                <h3 className="text-xl font-bold text-slate-900">{selectedImage.user_name}</h3>
                <div className="flex items-center gap-3 mt-1">
                  <p className="text-muted-foreground text-sm">
                    {new Date(selectedImage.uploaded_at).toLocaleString('id-ID')}
                  </p>
                  {selectedImage.active_percentage !== null && (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                      selectedImage.active_percentage >= 80 
                        ? 'bg-emerald-100 text-emerald-700'
                        : selectedImage.active_percentage >= 50
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      <div className="w-2.5 h-2.5 rounded-full" style={{ 
                        background: selectedImage.active_percentage >= 80 ? '#10b981' : selectedImage.active_percentage >= 50 ? '#f59e0b' : '#ef4444',
                        transform: 'rotate(45deg)'
                      }}></div>
                      {selectedImage.active_percentage.toFixed(0)}% Aktif
                    </span>
                  )}
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}